







































































import axios from 'axios';

import { get, map, uniq } from 'lodash';
import { Image, User } from '@/types/GlobalTypes';
import { createImageUploadFormData } from '@/utils/imageUpload';
import EventBus from '@/eventBus';
import CoEditor from '../../Molecules/co-editor/CoEditor.vue';
import CoThumbnail from '../../Molecules/co-thumbnail/CoThumbnail.vue';
import CoComment from '../co-comment/CoComment.vue';

interface Comment {
    ID?: string;
    Images: Image[];
    Author?: User | null;
    AuthorID?: string;
    PostID: string;
    PostType: string;
    CreatedAt?: number | string;
    UpdatedAt?: number | string;
    Text: string;
    Version: string;
}

export default {
    components: { CoEditor, CoThumbnail, CoComment },
    name: 'CoComments',
    props: {
        Me: {
            type: Object as () => User,
            required: true,
        },
        PostID: {
            type: String,
            required: true,
        },
        PostType: {
            type: String,
            required: true,
        },
        PostSlug: {
            type: String,
            required: false,
            default: '',
        },
        highlightCommentID: {
            type: String,
            required: false,
            default: null,
        },

        collapse: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            comments: null,
            sending: false,
            imageFiles: [],
            imageURLs: [],
            newComment: false,
            localCollapse: this.collapse,
            // Keep a newly streamed comment visible while the rest of the thread remains collapsed.
            latestStreamedCommentID: null,
        };
    },
    computed: {
        commentTopic() {
            return `COMMENTS_${this.PostID}`;
        },
        visibleComments() {
            if (!this.comments) return [];

            // if collapse is true, only show the first 1 comments and the highlighted comment
            if (this.localCollapse) {
                const latestStreamedComment = this.comments.find(
                    (comment) => comment.ID === this.latestStreamedCommentID
                );
                if (latestStreamedComment) return [latestStreamedComment];

                const highlightedComment = this.comments.find((c) => c.ID === this.highlightCommentID);
                if (highlightedComment) {
                    return [
                        highlightedComment,
                        ...this.comments.filter((c) => c.ID !== this.highlightCommentID).slice(0, 1),
                    ];
                }
                return this.comments.slice(0, 1);
            }

            return this.comments;
        },
        comment() {
            return {
                Version: '1',
                PostID: this.PostID,
                PostType: this.PostType,
                PostSlug: this.PostSlug,
            };
        },
    },
    mounted() {
        EventBus.$on(this.commentTopic, this.handleIncomingComment);
        this.getComments(this.PostID);
    },
    beforeDestroy() {
        EventBus.$off(this.commentTopic, this.handleIncomingComment);
    },
    watch: {
        comments: {
            handler(newVal, oldVal) {
                this.$emit('commentsLoaded', newVal);
            },
            deep: true,
        },
        collapse(newVal) {
            this.localCollapse = newVal;
        },
        PostID(newVal, oldVal) {
            if (oldVal) {
                EventBus.$off(`COMMENTS_${oldVal}`, this.handleIncomingComment);
            }

            EventBus.$on(`COMMENTS_${newVal}`, this.handleIncomingComment);
            this.getComments(newVal);
        },
    },
    methods: {
        expandEditor() {
            this.$refs.editor.expand();
        },
        showAllComments() {
            this.localCollapse = false;
            // Refresh the expanded thread so it includes comments received while it was collapsed.
            this.getComments(this.PostID);
            this.$emit('uncollapse');
        },
        sortComments(comments) {
            return [...comments].sort((left, right) => {
                const rightCreatedAt = Number(get(right, 'CreatedAt', 0));
                const leftCreatedAt = Number(get(left, 'CreatedAt', 0));

                if (rightCreatedAt !== leftCreatedAt) {
                    return rightCreatedAt - leftCreatedAt;
                }

                return Number(get(right, 'UpdatedAt', 0)) - Number(get(left, 'UpdatedAt', 0));
            });
        },
        async fetchUsersByIDs(userIDs) {
            if (!userIDs || userIDs.length < 1) return [];

            const users = await axios({
                method: 'POST',
                url: '/user/listbyids',
                data: {
                    IDS: map(userIDs, (i) => ({ ID: i })),
                },
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return map(users.data.Users, (i) => ({
                ...i,
                ...i.Profile,
            }));
        },
        async hydrateComments(comments) {
            if (!comments || comments.length < 1) return [];

            const userIDs = uniq(
                comments
                    .filter((comment) => !comment.Author)
                    .map((comment) => comment.AuthorID)
                    .filter(Boolean)
            );
            const usersMap = userIDs.length ? await this.fetchUsersByIDs(userIDs) : [];

            return this.sortComments(
                comments.map((comment) => ({
                    ...comment,
                    Author: comment.Author || usersMap.find((user) => user.ID === comment.AuthorID) || null,
                }))
            );
        },

        getComments(postID, newComment = false) {
            axios({
                method: 'GET',
                url: `/comment/list?postid=${postID}`,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(async (response) => {
                    const fetchedComments = map(get(response, 'data.objects', []), (i) => i.object);
                    this.comments = await this.hydrateComments(fetchedComments);
                    // set flag for new comment
                    if (newComment && this.comments.length > 0) {
                        this.comments[0].newComment = true;
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        async handleIncomingComment(comment) {
            if (!comment || comment.PostID !== this.PostID || !comment.ID) return;

            try {
                const hydratedComments = await this.hydrateComments([comment]);
                const incomingComment = hydratedComments[0];
                if (!incomingComment) return;

                // Keep the live stream idempotent because local creates can also trigger a list refresh.
                const existingComments = Array.isArray(this.comments) ? this.comments : [];
                const existingIndex = existingComments.findIndex(
                    (existingComment) => existingComment.ID === incomingComment.ID
                );
                const mergedComment = {
                    ...(existingIndex > -1 ? existingComments[existingIndex] : {}),
                    ...incomingComment,
                    newComment: existingIndex === -1,
                };

                if (existingIndex > -1) {
                    const nextComments = [...existingComments];
                    nextComments.splice(existingIndex, 1, mergedComment);
                    this.comments = this.sortComments(nextComments);
                } else {
                    this.comments = this.sortComments([mergedComment, ...existingComments]);
                    this.latestStreamedCommentID = incomingComment.ID;
                }
            } catch (error) {
                console.log(error);
            }
        },
        uploadImages(files) {
            if (!files || files.length === 0) return Promise.resolve([]);

            return Promise.all(
                files.map(async (file) => {
                    const data = await createImageUploadFormData(file.file);
                    const response = await axios({
                        method: 'POST',
                        url: '/upload/image/comment',
                        data,
                        withCredentials: true,
                    });

                    return get(response, 'data.url');
                })
            );
        },
        async createComment(commentData) {
            if (!commentData || typeof commentData !== 'object') return;
            this.sending = true;
            const images = await this.uploadImages(commentData.Files);

            const newComment = {
                ...this.comment,
                Text: commentData.Html,
                Images: images,
            };

            const data = JSON.stringify(newComment);

            axios({
                method: 'POST',
                url: '/comment',
                data,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    // update the comments
                    this.getComments(this.PostID);
                    // clear the editor
                    this.$refs.editor.clear(true);
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    this.sending = false;
                });
        },
        handleCommentDeleted(deletedComment) {
            // Remove the deleted comment from the comments array
            this.comments = this.comments.filter((comment) => comment.ID !== deletedComment.ID);
        },
    },
};
